import React from 'react';
import { Autocomplete, AutocompleteProps, Box, TextField, TextFieldProps } from '@mui/material';
import { CoreFormControlProps, CoreFormControl } from '../_core/core-form-control';
import { paperStyles, textFieldStyles } from './core-autocomplete.styles';
import { useScrollFetcher } from '../../hooks';
import { clsx } from '../../utils';
import { ArrowDownIcon } from '../../icons';

export interface CoreAutocompleteProps<
	Value,
	Multiple extends boolean | undefined,
	DisableClearable extends boolean | undefined,
	FreeSolo extends boolean | undefined = undefined
> extends Omit<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, 'renderInput'>,
		CoreFormControlProps {
	freeSolo?: FreeSolo;
	textFieldProps?: TextFieldProps;
	threshold?: number;
	hasMore?: boolean;
	placeholder?: string;
	fetchMore?(count: number): void;
}

export const CoreAutocomplete = <
	Value,
	Multiple extends boolean | undefined,
	DisableClearable extends boolean | undefined
>(
	props: CoreAutocompleteProps<Value, Multiple, DisableClearable>
) => {
	const {
		id,
		fullWidth,
		controlSx,
		label,
		labelSx,
		helperText,
		error,
		disabled,
		focused,
		requiredMark,
		threshold = 100,
		hasMore,
		loading,
		fetchMore,
		textFieldProps: { InputProps, ...textFieldProps } = {},
		componentsProps: { paper: paperProps, ...componentsProps } = {},
		popupIcon,
		options,
		placeholder,
		inputValue,
		...autocompleteProps
	} = props;

	const { containerRef, onScroll } = useScrollFetcher({
		threshold,
		fetchMore,
		itemsCount: options.length,
	});

	const shouldAttachScrollListener = hasMore && !loading;

	return (
		<CoreFormControl
			disabled={disabled}
			error={error}
			helperText={helperText}
			label={label}
			id={id}
			requiredMark={requiredMark}
			focused={focused}
			fullWidth={fullWidth}
			controlSx={controlSx}
			labelSx={labelSx}
		>
			<Autocomplete
				{...autocompleteProps}
				disabled={disabled}
				options={options}
				loading={loading}
				ListboxProps={{
					ref: containerRef,
					onScroll: shouldAttachScrollListener ? onScroll : undefined,
				}}
				disablePortal
				popupIcon={popupIcon || <Box component={ArrowDownIcon} sx={{ color: 'white' }} />}
				componentsProps={{
					...componentsProps,
					paper: {
						...(paperProps || {}),
						sx: [
							...(Array.isArray(paperStyles) ? paperStyles : [paperStyles]),
							...(paperProps ? (Array.isArray(paperProps.sx) ? paperProps.sx : [paperProps.sx]) : []),
						],
					},
				}}
				renderInput={params => (
					<TextField
						{...(params as TextFieldProps)}
						{...(textFieldProps || {})}
						fullWidth
						error={error}
						value={inputValue}
						InputProps={{
							...params.InputProps,
							...(InputProps || {}),
							startAdornment: (
								<>
									{InputProps?.startAdornment}
									{params.InputProps?.startAdornment}
								</>
							),
							value: inputValue,
							sx: clsx(textFieldStyles, InputProps?.sx),
						}}
						placeholder={placeholder}
					/>
				)}
			/>
		</CoreFormControl>
	);
};
